import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'
import * as api from '../api/client'
import { ApiError, describeError } from '../api/errors'
import { reportRequestFailure, useServerStatus } from '../api/serverStatus'
import { games as gamesApi } from '../api/rooms'
import { ProjectSocket, projectWsUrl } from '../api/ws'
import type {
  AnalysisOptions,
  AudioTrack,
  DetectLinesParams,
  DetectedSegment,
  Line,
  ProjectFull,
  ProjectMode,
  Role,
  TrackKind,
} from '../api/types'
import { useAnalysis } from '../analysis/useAnalysis'
import type { ApplyChoices } from '../analysis/useAnalysis'
import { PreviewMixer } from '../audio/preview'
import type { PreviewItem, PreviewState } from '../audio/preview'
import type { RecordedTake } from '../audio/recorder'
import { clearTrackBuffers, retainTrackBuffers } from '../audio/trackBuffers'
import { AnalysisPanel } from '../components/analysis/AnalysisPanel'
import { AnalysisPreviewBar } from '../components/analysis/AnalysisPreviewBar'
import { AnalysisReview } from '../components/analysis/AnalysisReview'
import { AnalyzeDialog } from '../components/analysis/AnalyzeDialog'
import { DetectLinesDialog, DetectPreviewBar } from '../components/DetectLines'
import type { DetectApplyOptions } from '../components/DetectLines'
import { LineList } from '../components/LineList'
import type { LinePatch } from '../components/LineList'
import { MergeLinesDialog } from '../components/MergeLinesDialog'
import type { MergeCandidate } from '../components/MergeLinesDialog'
import { RecordPanel } from '../components/RecordPanel'
import { RenderPanel } from '../components/RenderPanel'
import { ROLE_PALETTE, RoleEditor } from '../components/RoleEditor'
import { TakeList } from '../components/TakeList'
import { Timeline } from '../components/Timeline'
import { TracksPanel } from '../components/TracksPanel'
import type { TrackUploadRequest } from '../components/TracksPanel'
import { VideoPlayer } from '../components/VideoPlayer'
import { formatBytes, formatTime } from '../lib/format'
import { MAX_VIDEO_BYTES, VIDEO_ACCEPT, pickFile, probeVideoDuration } from '../lib/media'
import { navigate } from '../lib/router'
import { pushToast } from '../lib/toast'
import { findTrackByKind } from '../lib/tracks'

export type EditorPageProps = { projectId: string }

type ScenarioFile = {
  roles?: Array<{ id?: string; name?: string; color?: string }>
  lines?: Array<{ id?: string; roleId?: string; start?: number; end?: number; text?: string }>
}

function parseScenario(raw: string, projectId: string): { roles: Role[]; lines: Line[] } {
  const parsed = JSON.parse(raw) as unknown
  if (typeof parsed !== 'object' || parsed === null) throw new Error('Файл не похож на сценарий')
  const source = parsed as ScenarioFile
  if (!Array.isArray(source.roles) || !Array.isArray(source.lines)) {
    throw new Error('В файле нет полей roles и lines')
  }
  const roles: Role[] = source.roles.map((role, index) => ({
    id: typeof role.id === 'string' && role.id ? role.id : `role-${index + 1}`,
    projectId,
    name: typeof role.name === 'string' ? role.name : `Роль ${index + 1}`,
    color:
      typeof role.color === 'string' && role.color
        ? role.color
        : (ROLE_PALETTE[index % ROLE_PALETTE.length] as string),
  }))
  const lines: Line[] = source.lines.map((line, index) => ({
    id: typeof line.id === 'string' && line.id ? line.id : `line-${index + 1}`,
    projectId,
    roleId: typeof line.roleId === 'string' ? line.roleId : '',
    start: Number(line.start ?? 0),
    end: Number(line.end ?? 0),
    text: typeof line.text === 'string' ? line.text : '',
  }))
  return { roles, lines }
}

function newLineId(index: number): string {
  const uuid = globalThis.crypto?.randomUUID?.()
  return uuid ?? `auto-${Date.now()}-${index}`
}

/** 404/501 на новых эндпоинтах — бэкенд ещё не дошёл до этапа 4, а не поломка. */
function isMissingEndpoint(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 405 || err.status === 501)
}

export function EditorPage({ projectId }: EditorPageProps): JSX.Element {
  const status = useServerStatus()
  const [data, setData] = useState<ProjectFull | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [videoUploadProgress, setVideoUploadProgress] = useState<number | null>(null)
  const [takeUploadProgress, setTakeUploadProgress] = useState<number | null>(null)
  const [previewEnabled, setPreviewEnabled] = useState(false)
  const [previewState, setPreviewState] = useState<PreviewState>({
    enabled: false,
    mode: 'mute',
    loading: 0,
    failed: [],
    meStatus: 'absent',
    meError: null,
  })
  const [originalGainDb, setOriginalGainDb] = useState(-20)
  const [previewMeGainDb, setPreviewMeGainDb] = useState(0)
  const [recording, setRecording] = useState(false)
  const [videoVersion, setVideoVersion] = useState(0)

  // ------------------------------------------------------- дорожки и режим
  const [tracks, setTracks] = useState<AudioTrack[]>([])
  const [tracksAvailable, setTracksAvailable] = useState(true)
  const [tracksError, setTracksError] = useState<string | null>(null)
  const [tracksLoading, setTracksLoading] = useState(false)
  const [trackUploadProgress, setTrackUploadProgress] = useState<number | null>(null)
  const [mode, setMode] = useState<ProjectMode>('mute')

  // -------------------------------------------------- автоматическая разметка
  const [detectOpen, setDetectOpen] = useState(false)
  const [detectSegments, setDetectSegments] = useState<DetectedSegment[] | null>(null)
  const [detectApplying, setDetectApplying] = useState(false)

  // ------------------------------------------------- автоматический разбор
  const [analyzeOpen, setAnalyzeOpen] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)

  // ------------------------------------------- объединение и создание реплик
  const [mergeIds, setMergeIds] = useState<ReadonlySet<string>>(() => new Set<string>())
  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeBusy, setMergeBusy] = useState(false)
  /** Реплика, только что созданная протяжкой: фокус уходит в её текст. */
  const [focusLineId, setFocusLineId] = useState<string | null>(null)

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const mixerRef = useRef<PreviewMixer | null>(null)
  const socketRef = useRef<ProjectSocket | null>(null)
  const [socket, setSocket] = useState<ProjectSocket | null>(null)
  const analysis = useAnalysis(projectId, socket)

  // -------------------------------------------------------------- загрузка

  const load = useCallback(async (): Promise<void> => {
    try {
      const full = await api.getProject(projectId)
      setData(full)
      if (full.tracks) setTracks(full.tracks)
      setDuration((prev) => (full.project.videoDuration > 0 ? full.project.videoDuration : prev))
      setLoadError(null)
    } catch (err) {
      reportRequestFailure(err)
      setLoadError(describeError(err))
    }
  }, [projectId])

  /** Дорожек может не быть вовсе — тогда весь этап 4 деградирует до MVP. */
  const loadTracks = useCallback(async (): Promise<void> => {
    setTracksLoading(true)
    try {
      const list = await api.listTracks(projectId)
      setTracks(Array.isArray(list) ? list : [])
      setTracksAvailable(true)
      setTracksError(null)
    } catch (err) {
      setTracks([])
      setTracksAvailable(false)
      setTracksError(
        isMissingEndpoint(err)
          ? 'Сервер не отвечает на /api/projects/{id}/tracks — бэкенд ещё не обновлён до этапа 4.'
          : describeError(err),
      )
      if (!isMissingEndpoint(err)) reportRequestFailure(err)
    } finally {
      setTracksLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    setData(null)
    setTracks([])
    setDetectSegments(null)
    setMergeIds(new Set<string>())
    setMergeOpen(false)
    void load()
    void loadTracks()
  }, [load, loadTracks])

  // Сервер поля mode может ещё не отдавать — тогда локальный выбор не сбрасываем.
  const serverMode = data?.project.mode
  useEffect(() => {
    if (serverMode) setMode(serverMode)
  }, [serverMode])

  // Буферы дорожек большие: держим только то, что реально есть в проекте.
  useEffect(() => {
    retainTrackBuffers(tracks.map((track) => track.id))
  }, [tracks])

  useEffect(() => {
    return () => clearTrackBuffers()
  }, [])

  useEffect(() => {
    if (status.online === true && loadError !== null && data === null) void load()
  }, [status.online, loadError, data, load])

  // ------------------------------------------------------------------- WS

  useEffect(() => {
    const created = new ProjectSocket(projectWsUrl(projectId))
    socketRef.current = created
    setSocket(created)
    created.connect()
    const off = created.onEvent((event) => {
      if (event.type === 'take.added') {
        setData((prev) => (prev ? { ...prev, takes: upsert(prev.takes, event.take) } : prev))
      } else if (event.type === 'line.changed') {
        setData((prev) => (prev ? { ...prev, lines: upsert(prev.lines, event.line) } : prev))
      }
    })
    return () => {
      off()
      created.dispose()
      socketRef.current = null
      setSocket(null)
    }
  }, [projectId])

  // -------------------------------------------------------------- превью

  const activePreviewItems = useMemo<PreviewItem[]>(() => {
    if (!data) return []
    const lineById = new Map(data.lines.map((line) => [line.id, line]))
    const items: PreviewItem[] = []
    for (const take of data.takes) {
      if (!take.isActive) continue
      const line = lineById.get(take.lineId)
      if (!line) continue
      items.push({
        takeId: take.id,
        url: api.takeUrl(take.id),
        // Может быть отрицательным: дубль пишется с прероллом. Микшер в этом
        // случае вступает с середины буфера — так же, как ffmpeg с atrim.
        startAt: line.start + take.offset,
        gainDb: take.gain,
      })
    }
    return items
  }, [data])

  useEffect(() => {
    return () => {
      mixerRef.current?.dispose()
      mixerRef.current = null
    }
  }, [])

  const meTrack = useMemo(() => findTrackByKind(tracks, 'me'), [tracks])
  const dialogueTrack = useMemo(() => findTrackByKind(tracks, 'dialogue'), [tracks])
  const previewMode = mode === 'replace' ? 'replace' : 'mute'

  const ensureMixer = useCallback((): PreviewMixer | null => {
    if (mixerRef.current) return mixerRef.current
    const video = videoRef.current
    if (!video) return null
    const mixer = new PreviewMixer(video)
    mixer.setStateListener(setPreviewState)
    mixer.setOriginalGainDb(originalGainDb)
    mixer.setMeGainDb(previewMeGainDb)
    mixer.setMode(previewMode)
    mixer.setMeTrack(meTrack ? { id: meTrack.id, url: api.trackUrl(meTrack.id) } : null)
    mixer.setItems(activePreviewItems)
    mixerRef.current = mixer
    return mixer
  }, [activePreviewItems, originalGainDb, previewMeGainDb, previewMode, meTrack])

  useEffect(() => {
    mixerRef.current?.setItems(activePreviewItems)
  }, [activePreviewItems])

  useEffect(() => {
    mixerRef.current?.setOriginalGainDb(originalGainDb)
  }, [originalGainDb])

  useEffect(() => {
    mixerRef.current?.setMeGainDb(previewMeGainDb)
  }, [previewMeGainDb])

  useEffect(() => {
    mixerRef.current?.setMode(previewMode)
  }, [previewMode])

  useEffect(() => {
    mixerRef.current?.setMeTrack(meTrack ? { id: meTrack.id, url: api.trackUrl(meTrack.id) } : null)
  }, [meTrack])

  // Во время записи превью обязательно выключено: иначе микс уедет в микрофон.
  useEffect(() => {
    const mixer = mixerRef.current
    if (!mixer) return
    void mixer.setEnabled(previewEnabled && !recording)
  }, [previewEnabled, recording])

  const togglePreview = async (): Promise<void> => {
    const next = !previewEnabled
    if (next) {
      const mixer = ensureMixer()
      if (!mixer) {
        pushToast('error', 'Сначала загрузите видео')
        return
      }
      try {
        await mixer.setEnabled(true)
      } catch (err) {
        pushToast('error', describeError(err))
        return
      }
    } else {
      await mixerRef.current?.setEnabled(false)
    }
    setPreviewEnabled(next)
  }

  // -------------------------------------------------------------- хелперы

  const project = data?.project ?? null
  const roles = data?.roles ?? []
  const lines = data?.lines ?? []
  const takes = data?.takes ?? []

  const takeCountByLine = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const take of takes) counts[take.lineId] = (counts[take.lineId] ?? 0) + 1
    return counts
  }, [takes])

  const lineCountByRole = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const line of lines) counts[line.roleId] = (counts[line.roleId] ?? 0) + 1
    return counts
  }, [lines])

  const selectedLine = useMemo(
    () => lines.find((line) => line.id === selectedLineId) ?? null,
    [lines, selectedLineId],
  )
  const selectedRole = useMemo(
    () => roles.find((role) => role.id === selectedLine?.roleId),
    [roles, selectedLine],
  )
  const activeTakeCount = useMemo(() => takes.filter((take) => take.isActive).length, [takes])

  const seek = useCallback((time: number): void => {
    const video = videoRef.current
    if (!video) return
    const max = Number.isFinite(video.duration) ? video.duration : time
    video.currentTime = Math.max(0, Math.min(time, max))
    setCurrentTime(video.currentTime)
  }, [])

  const videoSrc = project?.videoFile ? `${api.videoUrl(projectId)}?v=${videoVersion}` : null
  const effectiveDuration = duration > 0 ? duration : (project?.videoDuration ?? 0)

  // Пробел — плей/пауза, если фокус не в поле ввода.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.code !== 'Space') return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName.toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || target?.isContentEditable) {
        return
      }
      const video = videoRef.current
      if (!video) return
      e.preventDefault()
      if (video.paused) void video.play().catch(() => undefined)
      else video.pause()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ------------------------------------------------------------- мутации

  const fail = (err: unknown): void => {
    reportRequestFailure(err)
    pushToast('error', describeError(err))
  }

  const uploadVideo = async (): Promise<void> => {
    const file = await pickFile(VIDEO_ACCEPT)
    if (!file) return
    if (file.size > MAX_VIDEO_BYTES) {
      pushToast('error', `Файл ${formatBytes(file.size)} — лимит 512 МБ`)
      return
    }
    const probed = await probeVideoDuration(file)
    setVideoUploadProgress(0)
    try {
      const { promise } = api.uploadVideo(projectId, file, probed, setVideoUploadProgress)
      const updated = await promise
      setData((prev) => (prev ? { ...prev, project: updated } : prev))
      setDuration(updated.videoDuration || probed || 0)
      setVideoVersion((v) => v + 1)
      pushToast('success', 'Видео загружено')
    } catch (err) {
      fail(err)
    } finally {
      setVideoUploadProgress(null)
    }
  }

  const createRole = async (name: string, color: string): Promise<void> => {
    try {
      const role = await api.createRole(projectId, { name, color })
      setData((prev) => (prev ? { ...prev, roles: upsert(prev.roles, role) } : prev))
    } catch (err) {
      fail(err)
    }
  }

  const patchRole = async (id: string, patch: { name?: string; color?: string }): Promise<void> => {
    try {
      const role = await api.patchRole(id, patch)
      setData((prev) => (prev ? { ...prev, roles: upsert(prev.roles, role) } : prev))
    } catch (err) {
      fail(err)
    }
  }

  const deleteRole = async (id: string): Promise<void> => {
    try {
      await api.deleteRole(id)
      // roleId у реплик обнуляется на сервере — перечитываем слепок.
      await load()
    } catch (err) {
      fail(err)
    }
  }

  const createLine = async (): Promise<void> => {
    const start = Number(currentTime.toFixed(2))
    const limit = effectiveDuration > 0 ? effectiveDuration : start + 2
    const end = Number(Math.min(start + 2, limit).toFixed(2))
    if (end <= start) {
      pushToast('error', 'До конца видео меньше кадра — отмотайте назад')
      return
    }
    const roleId = selectedLine?.roleId || roles[0]?.id || ''
    try {
      const line = await api.createLine(projectId, { roleId, start, end, text: '' })
      setData((prev) => (prev ? { ...prev, lines: upsert(prev.lines, line) } : prev))
      setSelectedLineId(line.id)
    } catch (err) {
      fail(err)
    }
  }

  /**
   * Реплика по границам протяжки на таймлайне (docs/SEGMENTATION.md,
   * «Объединение и создание реплик»). Роль — как у выделенной реплики или
   * первая в проекте: назначить её всё равно предлагается сразу, в открытой
   * карточке.
   */
  const createLineRange = async (start: number, end: number): Promise<void> => {
    const limit = effectiveDuration > 0 ? effectiveDuration + 1 : Number.POSITIVE_INFINITY
    const from = Number(Math.max(0, start).toFixed(2))
    const to = Number(Math.min(end, limit).toFixed(2))
    if (!(to > from)) {
      pushToast('error', 'Отрезок вышел за пределы видео — реплику не создать')
      return
    }
    const roleId = selectedLine?.roleId || roles[0]?.id || ''
    try {
      const line = await api.createLine(projectId, { roleId, start: from, end: to, text: '' })
      setData((prev) => (prev ? { ...prev, lines: upsert(prev.lines, line) } : prev))
      setSelectedLineId(line.id)
      setFocusLineId(line.id)
      seek(from)
    } catch (err) {
      fail(err)
    }
  }

  const toggleMerge = (lineId: string): void => {
    setMergeIds((prev) => {
      const next = new Set(prev)
      if (next.has(lineId)) next.delete(lineId)
      else next.add(lineId)
      return next
    })
  }

  const mergeGroup = useMemo(() => {
    if (mergeIds.size < 2) return []
    return [...lines]
      .sort((a, b) => a.start - b.start || a.end - b.end)
      .filter((line) => mergeIds.has(line.id))
  }, [lines, mergeIds])

  // Реплику могли удалить в другой вкладке — отметка на ней держаться не должна.
  useEffect(() => {
    setMergeIds((prev) => {
      if (prev.size === 0) return prev
      const alive = new Set(lines.map((line) => line.id))
      const next = new Set<string>()
      let dropped = false
      prev.forEach((id) => {
        if (alive.has(id)) next.add(id)
        else dropped = true
      })
      return dropped ? next : prev
    })
  }, [lines])

  const mergeCandidates = useMemo<MergeCandidate[]>(() => {
    const roleById = new Map(roles.map((role) => [role.id, role]))
    return mergeGroup.map((line) => {
      const role = roleById.get(line.roleId)
      return {
        id: line.id,
        start: line.start,
        end: line.end,
        roleId: line.roleId,
        roleName: role?.name ?? 'без роли',
        roleColor: role?.color ?? '',
        text: line.text,
        takes: takeCountByLine[line.id] ?? 0,
      }
    })
  }, [mergeGroup, roles, takeCountByLine])

  /**
   * Объединение выражается через `PUT /api/projects/{id}/lines`: отдельного
   * эндпоинта контракт не заводит (docs/SEGMENTATION.md). Дубли сохраняются
   * только у реплик с вернувшимися `id`, поэтому уцелеют записи первой
   * реплики, а записи остальных исчезнут — предупреждение об этом показано
   * в диалоге до операции.
   */
  const mergeLines = async (roleId: string): Promise<void> => {
    if (!data || mergeGroup.length < 2) return
    const first = mergeGroup[0]
    const last = mergeGroup[mergeGroup.length - 1]
    if (!first || !last) return
    const ids = new Set(mergeGroup.map((line) => line.id))
    const join = (parts: Array<string | undefined>): string =>
      parts
        .map((part) => (part ?? '').trim())
        .filter((part) => part.length > 0)
        .join(' ')
    const merged: Line = {
      ...first,
      roleId,
      start: first.start,
      end: last.end,
      text: join(mergeGroup.map((line) => line.text)),
      textOriginal: join(mergeGroup.map((line) => line.textOriginal)),
    }
    const nextLines = lines
      .filter((line) => !ids.has(line.id) || line.id === first.id)
      .map((line) => (line.id === first.id ? merged : line))
    setMergeBusy(true)
    try {
      const full = await api.replaceScenario(projectId, { roles: data.roles, lines: nextLines })
      setData(full)
      if (full.tracks) setTracks(full.tracks)
      setSelectedLineId(merged.id)
      setMergeIds(new Set<string>())
      setMergeOpen(false)
      pushToast('success', `Объединено реплик: ${mergeGroup.length}`)
    } catch (err) {
      fail(err)
    } finally {
      setMergeBusy(false)
    }
  }

  const patchLine = async (id: string, patch: LinePatch): Promise<void> => {
    try {
      const line = await api.patchLine(id, patch)
      setData((prev) => (prev ? { ...prev, lines: upsert(prev.lines, line) } : prev))
    } catch (err) {
      fail(err)
    }
  }

  const deleteLine = async (id: string): Promise<void> => {
    try {
      await api.deleteLine(id)
      setData((prev) =>
        prev
          ? {
              ...prev,
              lines: prev.lines.filter((line) => line.id !== id),
              takes: prev.takes.filter((take) => take.lineId !== id),
            }
          : prev,
      )
      if (selectedLineId === id) setSelectedLineId(null)
      setMergeIds((prev) => {
        if (!prev.has(id)) return prev
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    } catch (err) {
      fail(err)
    }
  }

  const publishGame = async (): Promise<void> => {
    if (!data) return
    if (data.lines.length === 0) {
      pushToast('error', 'Нельзя опубликовать проект без реплик')
      return
    }
    try {
      const game = await gamesApi.publish(projectId, {})
      pushToast(
        'success',
        `«${game.title}» в каталоге: ролей ${game.rolesCount}, реплик ${game.linesCount}. Публикация — снимок, дальнейшая правка проекта её не изменит.`,
      )
    } catch (err) {
      pushToast('error', describeError(err))
    }
  }

  const handleRecorded = async (result: RecordedTake): Promise<void> => {
    const line = selectedLine
    if (!line) return
    setTakeUploadProgress(0)
    try {
      const { promise } = api.uploadTake(
        line.id,
        {
          blob: result.blob,
          filename: result.filename,
          duration: result.duration,
          offset: result.offset,
          author: 'local',
        },
        setTakeUploadProgress,
      )
      const take = await promise
      setData((prev) =>
        prev
          ? {
              ...prev,
              takes: upsert(
                prev.takes.map((t) =>
                  t.lineId === take.lineId && take.isActive ? { ...t, isActive: false } : t,
                ),
                take,
              ),
            }
          : prev,
      )
      pushToast('success', 'Дубль записан')
    } catch (err) {
      fail(err)
    } finally {
      setTakeUploadProgress(null)
    }
  }

  const setActiveTake = async (takeId: string): Promise<void> => {
    try {
      const take = await api.patchTake(takeId, { isActive: true })
      setData((prev) =>
        prev
          ? {
              ...prev,
              takes: upsert(
                prev.takes.map((t) => (t.lineId === take.lineId ? { ...t, isActive: false } : t)),
                take,
              ),
            }
          : prev,
      )
    } catch (err) {
      fail(err)
    }
  }

  const patchTake = async (
    takeId: string,
    patch: { gain?: number; offset?: number },
  ): Promise<void> => {
    try {
      const take = await api.patchTake(takeId, patch)
      setData((prev) => (prev ? { ...prev, takes: upsert(prev.takes, take) } : prev))
    } catch (err) {
      fail(err)
    }
  }

  const deleteTake = async (takeId: string): Promise<void> => {
    try {
      await api.deleteTake(takeId)
      await load()
    } catch (err) {
      fail(err)
    }
  }

  const importScenario = async (): Promise<void> => {
    const file = await pickFile('.json,application/json')
    if (!file) return
    if (!window.confirm('Импорт заменит все роли и реплики проекта. Продолжить?')) return
    try {
      const payload = parseScenario(await file.text(), projectId)
      const full = await api.replaceScenario(projectId, payload)
      setData(full)
      setSelectedLineId(null)
      pushToast('success', `Сценарий импортирован: ${full.lines.length} реплик`)
    } catch (err) {
      fail(err)
    }
  }

  // ------------------------------------------------------ дорожки и режим

  const changeMode = async (next: ProjectMode): Promise<void> => {
    const previous = mode
    setMode(next)
    try {
      const updated = await api.patchProject(projectId, { mode: next })
      setData((prev) => (prev ? { ...prev, project: updated } : prev))
      if (updated.mode === undefined) {
        pushToast(
          'info',
          'Сервер не вернул режим проекта — он применён только в браузере. Рендер всё равно получит режим явным параметром.',
        )
      } else if (updated.mode !== next) {
        setMode(updated.mode)
        pushToast('error', 'Сервер не принял выбранный режим')
      }
    } catch (err) {
      setMode(previous)
      fail(err)
    }
  }

  const patchTrack = async (
    id: string,
    patch: { kind?: TrackKind; label?: string },
  ): Promise<void> => {
    try {
      const track = await api.patchTrack(id, patch)
      setTracks((prev) => upsert(prev, track))
    } catch (err) {
      fail(err)
    }
  }

  const deleteTrack = async (id: string): Promise<void> => {
    try {
      await api.deleteTrack(id)
      setTracks((prev) => prev.filter((track) => track.id !== id))
    } catch (err) {
      fail(err)
    }
  }

  const uploadTrack = async (request: TrackUploadRequest): Promise<void> => {
    setTrackUploadProgress(0)
    try {
      const { promise } = api.uploadTrack(
        projectId,
        request.file,
        { kind: request.kind, label: request.label },
        setTrackUploadProgress,
      )
      const track = await promise
      setTracks((prev) => upsert(prev, track))
      pushToast('success', 'Дорожка загружена')
    } catch (err) {
      fail(err)
    } finally {
      setTrackUploadProgress(null)
    }
  }

  // ------------------------------------------------ автоматическая разметка

  const runDetect = async (params: DetectLinesParams): Promise<DetectedSegment[]> => {
    const result = await api.detectLines(projectId, params)
    const raw = Array.isArray(result?.segments) ? result.segments : []
    return raw
      .filter(
        (segment) =>
          Number.isFinite(segment.start) && Number.isFinite(segment.end) && segment.end > segment.start,
      )
      .map((segment) => ({ start: Math.max(0, segment.start), end: segment.end }))
      .sort((a, b) => a.start - b.start)
  }

  const applyDetected = async ({ roleId, mode: applyMode }: DetectApplyOptions): Promise<void> => {
    if (!detectSegments || !data) return
    setDetectApplying(true)
    try {
      // Ограничение сервера: end <= videoDuration + 1 (docs/API.md, «Реплики»).
      const limit = effectiveDuration > 0 ? effectiveDuration + 1 : Number.POSITIVE_INFINITY
      const created: Line[] = detectSegments
        .map((segment, index) => ({
          id: newLineId(index),
          projectId,
          roleId,
          start: Number(Math.max(0, segment.start).toFixed(2)),
          end: Number(Math.min(segment.end, limit).toFixed(2)),
          text: '',
        }))
        .filter((line) => line.end > line.start)
      if (created.length === 0) {
        pushToast('error', 'Все сегменты вышли за пределы видео — применять нечего')
        return
      }
      const lines = applyMode === 'replace' ? created : [...data.lines, ...created]
      const full = await api.replaceScenario(projectId, { roles: data.roles, lines })
      setData(full)
      if (full.tracks) setTracks(full.tracks)
      setSelectedLineId(null)
      setDetectSegments(null)
      pushToast('success', `Разметка применена: ${created.length} реплик`)
    } catch (err) {
      fail(err)
    } finally {
      setDetectApplying(false)
    }
  }

  // ------------------------------------------------ автоматический разбор

  // Фокус в текст — одноразовый: карточку могли закрыть, а флаг остался бы
  // и уводил бы курсор при следующем открытии.
  useEffect(() => {
    if (focusLineId !== null && selectedLineId !== focusLineId) setFocusLineId(null)
  }, [focusLineId, selectedLineId])

  const analysisDraft = analysis.draft
  const hadDraftRef = useRef(false)

  // Черновик появился — сразу показываем таблицу: ради неё разбор и затевался.
  // Реагируем только на появление, иначе правка строки снова раскрывала бы
  // свёрнутую таблицу.
  useEffect(() => {
    const has = analysisDraft !== null
    if (has && !hadDraftRef.current) setReviewOpen(true)
    if (!has) setReviewOpen(false)
    hadDraftRef.current = has
  }, [analysisDraft])

  const analysisSegments = useMemo(
    () =>
      analysisDraft
        ? analysisDraft.lines.map((line) => ({
            start: line.start,
            end: line.end,
            // Невербальную помечаем прямо в подсказке: на таймлайне у неё
            // нет текста, и без метки блок выглядит пустым по ошибке.
            label:
              line.kind === 'nonverbal'
                ? `◍ ${line.text || 'без слов'}`
                : line.text || line.textOriginal,
          }))
        : null,
    [analysisDraft],
  )

  const startAnalysis = async (options: AnalysisOptions): Promise<void> => {
    await analysis.start(options)
    setAnalyzeOpen(false)
    pushToast('info', 'Разбор поставлен в очередь. Это надолго — вкладку можно закрыть.')
  }

  const applyAnalysis = async (choices: ApplyChoices): Promise<void> => {
    try {
      const full = await analysis.apply(choices, effectiveDuration)
      setSelectedLineId(null)
      setReviewOpen(false)
      if (full && full.project) {
        setData(full)
        if (full.tracks) setTracks(full.tracks)
        else if (choices.tracks) void loadTracks()
      } else {
        await load()
        await loadTracks()
      }
      pushToast('success', 'Разбор применён. Проверьте реплики — это машинный черновик.')
    } catch {
      // Текст ошибки уже в analysis.error — второй тост не нужен.
    }
  }

  const dismissAnalysis = (): void => {
    analysis.dismiss()
    setReviewOpen(false)
  }

  // ---------------------------------------------------------------- вывод

  if (loadError && !data) {
    return (
      <div className="page">
        <div className="empty">
          <p>Не удалось открыть проект.</p>
          <p className="faint">{loadError}</p>
          <div className="row" style={{ justifyContent: 'center' }}>
            <button type="button" onClick={() => void load()}>
              Повторить
            </button>
            <button type="button" className="ghost" onClick={() => navigate({ name: 'projects' })}>
              К списку проектов
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="page">
        <div className="empty">Загружаю проект…</div>
      </div>
    )
  }

  return (
    <div className="page page-wide">
      <div className="row wrap" style={{ marginBottom: 12 }}>
        <button type="button" className="ghost" onClick={() => navigate({ name: 'projects' })}>
          ← Проекты
        </button>
        <h1 style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {project.title}
        </h1>
        <span className="faint mono">{formatTime(effectiveDuration)}</span>
        <span className="spacer" />
        <button
          type="button"
          className="primary"
          onClick={() => navigate({ name: 'play', projectId, stage: 'setup' })}
          title="Озвучивать по репликам, без таймлайна и слайдеров"
        >
          ▶ Играть
        </button>
        <button
          type="button"
          onClick={() => void publishGame()}
          title="Сделать из проекта озвучку для игры с друзьями по ссылке"
        >
          Опубликовать как озвучку
        </button>
        <button type="button" onClick={() => void uploadVideo()} disabled={videoUploadProgress !== null}>
          {project.videoFile ? 'Заменить видео' : 'Загрузить видео'}
        </button>
        <a className="badge" href={api.exportUrl(projectId, true)} download>
          ⤓ Сценарий JSON
        </a>
        <a className="badge" href={api.exportUrl(projectId, false)} download>
          ⤓ Экспорт ZIP
        </a>
        <button type="button" className="ghost" onClick={() => void importScenario()}>
          ⤒ Импорт сценария
        </button>
      </div>

      {videoUploadProgress !== null ? (
        <div className="progress" style={{ marginBottom: 12 }}>
          <i style={{ width: `${Math.round(videoUploadProgress * 100)}%` }} />
        </div>
      ) : null}

      {analysisDraft && reviewOpen ? (
        <AnalysisReview
          draft={analysisDraft}
          videoDuration={effectiveDuration}
          currentTime={currentTime}
          disabled={analysis.applying}
          onChange={analysis.setDraft}
          onSeek={seek}
          onCollapse={() => setReviewOpen(false)}
          onDismiss={dismissAnalysis}
        />
      ) : null}

      <div className="editor">
        <div className="editor-col">
          <VideoPlayer
            src={videoSrc}
            videoRef={videoRef}
            currentTime={currentTime}
            duration={effectiveDuration}
            onTimeChange={setCurrentTime}
            onDurationChange={setDuration}
          />

          <div className="panel">
            <Timeline
              duration={effectiveDuration}
              lines={lines}
              roles={roles}
              currentTime={currentTime}
              selectedLineId={selectedLineId}
              takeCountByLine={takeCountByLine}
              onSeek={seek}
              onSelectLine={setSelectedLineId}
              previewSegments={detectSegments ?? analysisSegments ?? undefined}
              previewCaption={detectSegments ? 'разметка' : 'разбор'}
              onCreateRange={(start, end) => void createLineRange(start, end)}
            />

            {detectSegments ? (
              <DetectPreviewBar
                segments={detectSegments}
                roles={roles}
                existingLines={lines.length}
                applying={detectApplying}
                onApply={(options) => void applyDetected(options)}
                onCancel={() => setDetectSegments(null)}
              />
            ) : analysisDraft ? (
              <AnalysisPreviewBar
                draft={analysisDraft}
                videoDuration={effectiveDuration}
                existingLines={lines.length}
                existingTakes={takes.length}
                reviewOpen={reviewOpen}
                applying={analysis.applying}
                onToggleReview={() => setReviewOpen((open) => !open)}
                onApply={(choices) => void applyAnalysis(choices)}
                onDismiss={dismissAnalysis}
              />
            ) : (
              <div className="row wrap" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  disabled={!tracksAvailable || tracks.length === 0}
                  onClick={() => setDetectOpen(true)}
                  title={
                    tracks.length === 0
                      ? 'Нужна дорожка с оригинальными репликами'
                      : 'Найти границы фраз по дорожке реплик'
                  }
                >
                  ⌁ Разметить автоматически
                </button>
                <span className="faint">
                  {dialogueTrack
                    ? 'Границы фраз ищет ffmpeg по дорожке реплик; результат можно отменить.'
                    : 'Разметка идёт по чистой дорожке реплик — на смешанном звуке детектор ошибается.'}
                </span>
              </div>
            )}
          </div>

          <AnalysisPanel
            analyzer={status.analyzer}
            serverOnline={status.online}
            hasVideo={Boolean(project.videoFile)}
            controller={analysis}
            reviewOpen={reviewOpen}
            onOpenDialog={() => setAnalyzeOpen(true)}
            onOpenReview={() => setReviewOpen(true)}
          />

          <TracksPanel
            tracks={tracks}
            mode={mode}
            available={tracksAvailable}
            unavailableReason={tracksError}
            loading={tracksLoading}
            uploadProgress={trackUploadProgress}
            meStatus={previewState.meStatus}
            meError={previewState.meError}
            onReload={() => void loadTracks()}
            onModeChange={(next) => void changeMode(next)}
            onPatchTrack={(id, patch) => void patchTrack(id, patch)}
            onDeleteTrack={(id) => void deleteTrack(id)}
            onUploadTrack={(request) => void uploadTrack(request)}
          />

          <section className="panel">
            <header>
              <h2>Превью микса</h2>
              {previewState.loading > 0 ? (
                <span className="badge">декодирую дубли: {previewState.loading}…</span>
              ) : null}
              {previewMode === 'replace' && previewState.meStatus === 'loading' ? (
                <span className="badge" style={{ color: 'var(--warning)' }}>
                  декодирую M&E…
                </span>
              ) : null}
            </header>
            <div className="stack">
              <div className="row wrap">
                <button
                  type="button"
                  className={previewEnabled ? 'primary' : ''}
                  onClick={() => void togglePreview()}
                  disabled={!videoSrc || recording}
                >
                  {previewEnabled ? '⏹ Выключить превью' : '▶ Включить превью'}
                </button>
                <span className="faint">
                  {activePreviewItems.length} активных дублей подмешивается
                </span>
              </div>

              {previewMode === 'replace' ? (
                <>
                  {!meTrack ? (
                    <div className="notice warn">
                      <strong>Дорожки M&E нет</strong>
                      <span className="faint">
                        Превью в режиме замены нечего играть. Назначьте тип «M&E» одной из дорожек
                        или загрузите её отдельным файлом.
                      </span>
                    </div>
                  ) : null}
                  {previewState.meStatus === 'error' ? (
                    <p className="faint">
                      M&E не декодировалась
                      {previewState.meError ? `: ${previewState.meError}` : ''}. Браузер может не
                      понимать формат — надёжнее всего WAV.
                    </p>
                  ) : null}
                  <div>
                    <label htmlFor="preview-me-gain">
                      Дорожка M&E: <span className="mono">{previewMeGainDb} дБ</span>
                    </label>
                    <input
                      id="preview-me-gain"
                      type="range"
                      min={-24}
                      max={12}
                      step={1}
                      value={previewMeGainDb}
                      onChange={(e) => setPreviewMeGainDb(Number(e.currentTarget.value))}
                    />
                  </div>
                  <p className="faint">
                    Видео играет с выключённым звуком, основа берётся из M&E. Дорожка декодируется
                    целиком — на длинных файлах это заметно долго и тяжело по памяти.
                  </p>
                </>
              ) : (
                <div>
                  <label htmlFor="preview-gain">
                    Оригинальная дорожка: <span className="mono">{originalGainDb} дБ</span>
                  </label>
                  <input
                    id="preview-gain"
                    type="range"
                    min={-60}
                    max={0}
                    step={1}
                    value={originalGainDb}
                    onChange={(e) => setOriginalGainDb(Number(e.currentTarget.value))}
                  />
                </div>
              )}

              {previewState.failed.length > 0 ? (
                <p className="faint">
                  Не удалось декодировать {previewState.failed.length} дубл(ей) — браузер может не
                  понимать их формат.
                </p>
              ) : null}
              <p className="faint">
                Превью собирается в браузере через Web Audio. Финальную дорожку всё равно склеивает
                сервер.
              </p>
            </div>
          </section>

          <RenderPanel
            projectId={projectId}
            socket={socket}
            ffmpegAvailable={status.ffmpeg}
            activeTakes={activeTakeCount}
            mode={mode}
            meTrack={meTrack}
          />
        </div>

        <div className="editor-col">
          <RoleEditor
            roles={roles}
            lineCountByRole={lineCountByRole}
            onCreate={(name, color) => void createRole(name, color)}
            onPatch={(id, patch) => void patchRole(id, patch)}
            onDelete={(id) => void deleteRole(id)}
          />

          <LineList
            lines={lines}
            roles={roles}
            takeCountByLine={takeCountByLine}
            selectedLineId={selectedLineId}
            currentTime={currentTime}
            videoDuration={effectiveDuration}
            onSelect={setSelectedLineId}
            onSeek={seek}
            onCreate={() => void createLine()}
            onPatch={(id, patch) => void patchLine(id, patch)}
            onDelete={(id) => void deleteLine(id)}
            mergeSelection={mergeIds}
            onToggleMerge={toggleMerge}
            onClearMerge={() => setMergeIds(new Set<string>())}
            onMerge={() => setMergeOpen(true)}
            autoFocusLineId={focusLineId}
            renderExtra={(line) => (
              <div className="stack">
                <label>Дубли ({takeCountByLine[line.id] ?? 0})</label>
                <TakeList
                  takes={takes.filter((take) => take.lineId === line.id)}
                  onSetActive={(id) => void setActiveTake(id)}
                  onPatch={(id, patch) => void patchTake(id, patch)}
                  onDelete={(id) => void deleteTake(id)}
                />
              </div>
            )}
          />

          <RecordPanel
            line={selectedLine}
            role={selectedRole}
            videoRef={videoRef}
            hasVideo={Boolean(videoSrc)}
            uploadProgress={takeUploadProgress}
            guideTrack={dialogueTrack}
            onRecorded={handleRecorded}
            onRecordingChange={setRecording}
          />
        </div>
      </div>

      {detectOpen ? (
        <DetectLinesDialog
          tracks={tracks}
          onRun={runDetect}
          onDetected={(segments) => {
            setDetectSegments(segments)
            setDetectOpen(false)
            pushToast('info', `Найдено сегментов: ${segments.length}. Проверьте на таймлайне.`)
          }}
          onClose={() => setDetectOpen(false)}
        />
      ) : null}

      {mergeOpen && mergeCandidates.length > 1 ? (
        <MergeLinesDialog
          candidates={mergeCandidates}
          roleOptions={roles.map((role) => ({ id: role.id, name: role.name, color: role.color }))}
          takesAtRisk
          busy={mergeBusy}
          onConfirm={(roleId) => void mergeLines(roleId)}
          onCancel={() => setMergeOpen(false)}
        />
      ) : null}

      {analyzeOpen ? (
        <AnalyzeDialog
          analyzer={status.analyzer}
          videoDuration={effectiveDuration}
          existingLines={lines.length}
          onRun={startAnalysis}
          onClose={() => setAnalyzeOpen(false)}
        />
      ) : null}
    </div>
  )
}

/**
 * Вставляет или обновляет запись по идентификатору.
 *
 * Ответы на собственные запросы тоже проходят через него, а не добавляются
 * в список напрямую: сервер рассылает событие об изменении ДО того, как
 * ответит на запрос, поэтому подписка нередко успевает вставить объект
 * первой. Простое добавление в этом случае положило бы его второй раз —
 * в интерфейсе появлялся дубль, которого нет в базе.
 */
function upsert<T extends { id: string }>(items: T[], next: T): T[] {
  let found = false
  const result = items.map((item) => {
    if (item.id !== next.id) return item
    found = true
    return next
  })
  return found ? result : [...result, next]
}
